import { Link, useLocation } from 'react-router';

import { useAuth } from '@/auth/useAuth';
import { Icon } from '@/ui/Icon';
import { locatePath, type NavLocation } from './navigation';

interface Crumb {
  readonly label: string;
  /** Where the crumb goes; absent for the page you are on and for a name that is only a heading. */
  readonly to?: string;
  readonly current?: boolean;
}

/**
 * The crumbs after the workspace: the destination, then the view when that adds a name.
 *
 * On a view's own page the last crumb is the page you are on. On a record beneath it the view is a
 * link back to its list, and no crumb claims to be the page, because the record is the page and
 * the registry does not know its name.
 */
function crumbsFor({ destination, view, isViewRoute }: NavLocation): readonly Crumb[] {
  const own: Crumb = isViewRoute
    ? { label: view.label, current: true }
    : { label: view.label, to: view.paths[0] };
  if (destination.views.length === 1 || view.label === destination.label) return [own];
  return [{ label: destination.label }, own];
}

function CrumbText({ crumb }: { readonly crumb: Crumb }): React.JSX.Element {
  if (crumb.to !== undefined) return <Link to={crumb.to}>{crumb.label}</Link>;
  // aria-current marks the last crumb as the page you are on, which is what stops a screen
  // reader reading it as a link to somewhere else.
  if (crumb.current === true) return <span aria-current="page">{crumb.label}</span>;
  return <span className="fo-strip__group">{crumb.label}</span>;
}

/**
 * Where you are, from the workspace down to the page.
 *
 * @returns the breadcrumb landmark
 */
export function Breadcrumb(): React.JSX.Element {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const located = locatePath(pathname);
  const crumbs =
    located === undefined || located.destination.id === 'today' ? [] : crumbsFor(located);

  return (
    <nav className="fo-strip__breadcrumb" aria-label="Breadcrumb">
      <ol>
        <li>
          <Link to="/">{user?.workspaceName ?? 'Workspace'}</Link>
        </li>
        {crumbs.map((crumb) => (
          <li key={crumb.label}>
            <Icon name="chevron-right" size="0.9em" className="fo-strip__separator" />
            <CrumbText crumb={crumb} />
          </li>
        ))}
      </ol>
    </nav>
  );
}
